<template>
  <div
    v-if="Object.keys(tabDefinition).length > 0"
    class="tabs-container"
  >
    <button
      class="tab-button"
      :class="{ active: activeTab === CAMPAIGN_TABS.AVAILABLE.text }"
      type="button"
      @click="handleTabClick(CAMPAIGN_TABS.AVAILABLE)"
    >
      <span class="label">{{ getTabLabel(CAMPAIGN_TABS.AVAILABLE) }}</span>
      <span class="count">({{ campaignCounts.availableCount }})</span>
    </button>
    <button
      class="tab-button"
      :class="{ active: activeTab === CAMPAIGN_TABS.JOINED.text }"
      type="button"
      @click="handleTabClick(CAMPAIGN_TABS.JOINED)"
    >
      <span class="label">{{ getTabLabel(CAMPAIGN_TABS.JOINED) }}</span>
      <span class="count">({{ campaignCounts.joinedCount }})</span>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { sendTabClickEvent } from '@/utils/ga4/helper';
import { useCategoryCommissionCampaignsCount } from '../services';
import { CAMPAIGN_TABS } from '../constants';

const props = defineProps({
  activeTab: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:activeTab']);

const store = useStore();
const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));

const getTabLabel = (tab) =>
  tabDefinition.value?.find?.((d) => d.code === tab.code)?.title ||
  tab.defaultTitle;

const getTabCampaignCount = (tab) => {
  if (tab.text === CAMPAIGN_TABS.AVAILABLE.text)
    return campaignCounts.value.availableCount;
  if (tab.text === CAMPAIGN_TABS.JOINED.text)
    return campaignCounts.value.joinedCount;
  return 0;
};

const handleTabClick = (tab) => {
  emit('update:activeTab', tab.text);
  sendTabClickEvent({
    button_name: getTabLabel(tab),
    campaign_count: getTabCampaignCount(tab),
    page_type: 'Kategori Komisyon Kampanyaları',
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
};

const campaignCounts = ref({
  availableCount: 0,
  joinedCount: 0,
});

const tabDefinition = ref({});

const { data: countsData, fetchData: fetchCountsData } =
  useCategoryCommissionCampaignsCount();

const getCountsData = async () => {
  await fetchCountsData();
  const tabs = countsData.value?.tabDefinition ?? [];
  tabDefinition.value = tabs;
  campaignCounts.value = {
    availableCount: tabs.find((t) => t.code === 'available')?.count ?? 0,
    joinedCount: tabs.find((t) => t.code === 'joined')?.count ?? 0,
  };
};

watch(
  () => props.activeTab,
  async () => {
    await getCountsData();
  },
  { immediate: true },
);

defineExpose({
  getCountsData,
});
</script>
<style lang="scss" scoped>
@import '@/assets/styles/variables';

.tabs-container {
  display: flex;
  padding: 16px 24px;
  border-bottom: 0.5px solid $colorN30;
  gap: 12px;

  .tab-button {
    display: flex;
    min-height: 64px;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    border: 0.5px solid $colorD30;
    border-radius: 6px;
    background: $colorN00;
    color: $colorN80;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    gap: 4px;
    transition: all 0.2s;

    .label {
      color: $colorN80;
      cursor: pointer;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 18px;
      text-align: center;
    }

    .count {
      color: $colorN60;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 18px;
      text-align: center;
    }

    &.active {
      border: 2px solid $colorM50;
      background: $colorM05;
      color: $colorN80;
      font-weight: 600;

      .label,
      .count {
        color: $colorN80;
      }
    }
  }
}
</style>
